// Protocolo do Hábito — o protocolo de retorno.
//
// "O dia seguinte à falha é o momento mais importante do programa." Entre 53
// intervenções testadas com 61.293 pessoas, a vencedora foi premiar quem volta
// depois de faltar — não quem nunca falta.
//
// Duas regras governam esta tela:
//
//   · O dano do lapso vem da INTERPRETAÇÃO, não do lapso. A percepção de
//     violação precisa ser desarmada ANTES de se instalar, então a tela nunca
//     abre com a acusação: abre com o reencontro.
//   · Nunca mostrar zero. O que aparece é o que ficou — dias lidos e melhor
//     marca são permanentes; só o contador reinicia.
//
// O tom do roteiro de autocompaixão segue o script validado por Adams & Leary
// (2007): normalizar ("muita gente"), desarmar o julgamento ("não pegue pesado
// consigo") e seguir em frente sem pedir desculpa nem exigir promessa.

import React from 'react';
import { BookCover } from '../common';
import { Icon } from '../icons';
import { EstadoHabito } from './api';

const serif = 'Instrument Serif, Georgia, serif';
const CREME = '#F5F1EA';

const eyebrow: React.CSSProperties = {
  fontSize: 10.5, letterSpacing: '0.16em', textTransform: 'uppercase', fontWeight: 600,
};

export const TelaDeRetorno: React.FC<{
  estado: EstadoHabito;
  onAbrirLivro: () => void;
  onDepois: () => void;
}> = ({ estado, onAbrirLivro, onDepois }) => {
  // O número da tela é DIAS SEM LER, não `lapso.dias`. O lapso é encurtado pela
  // proteção (ela vira parede no caminho de volta), então um sumiço de 7 dias
  // virava 6 e desligava justamente o texto escrito para ele. Dias sem ler é o
  // fato; o lapso é contabilidade.
  const dias = estado.diasSemLer ?? estado.lapso?.dias ?? 1;
  const { sequencia, livro, contrato } = estado;
  const voltas = estado.retornos?.total || 0;

  // Uma semana inteira perdida é outra coisa: aí a evidência mostra prejuízo real
  // à aquisição. O texto muda, mas o acolhimento não.
  const sumicoLongo = dias >= 7;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9650, background: '#141110',
      display: 'flex', flexDirection: 'column', maxWidth: 460, margin: '0 auto',
      overflowY: 'auto', animation: 'e2FadeUp .32s ease both',
    }}>
      <style>{`
        @keyframes habVolta { 0% { transform: scale(.55); opacity: 0 } 60% { opacity: .5 } 100% { transform: scale(2.2); opacity: 0 } }
        @keyframes habSobe2 { 0% { transform: translateY(12px); opacity: 0 } 100% { transform: translateY(0); opacity: 1 } }
      `}</style>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 'calc(30px + env(safe-area-inset-top,0px)) 26px 20px' }}>

        <div style={{ position: 'relative', width: 96, height: 96, display: 'grid', placeItems: 'center', marginBottom: 22 }}>
          {[0, 0.7].map((d) => (
            <div key={d} style={{
              position: 'absolute', inset: 18, borderRadius: '50%', border: '1px solid var(--ember)',
              animation: `habVolta 3s ${d}s ease-out infinite`,
            }} />
          ))}
          <Icon name="flame" size={44} color="var(--ember)" />
        </div>

        <div className="mono" style={{ ...eyebrow, color: 'rgba(245,241,234,0.45)', animation: 'habSobe2 .5s .05s both' }}>
          O reencontro
        </div>

        {/* Abre com o reencontro, nunca com a acusação. */}
        <h2 style={{
          fontFamily: serif, fontSize: 34, lineHeight: 1.08, color: CREME,
          margin: '10px 0 14px', animation: 'habSobe2 .5s .15s both',
        }}>
          {voltas >= 2 ? <>Você <i>voltou de novo</i>.</> : <>Que bom que<br /><i>você voltou</i>.</>}
        </h2>

        <p style={{ fontSize: 15.5, lineHeight: 1.62, color: 'rgba(245,241,234,0.72)', margin: 0, animation: 'habSobe2 .5s .25s both' }}>
          {/* A partir da 3ª volta o reconhecimento deixa de ser sobre a
              categoria ("acontece com todo mundo") e passa a ser sobre ela.
              Repetir a mesma frase genérica nove vezes deixa de ser acolhimento
              e vira prova de que o app não está olhando. */}
          {voltas >= 4
            ? `Esta é a sua ${voltas + 1}ª volta. Voltar já é o que você faz de melhor aqui — o que ainda não achou o lugar é o intervalo.`
            : voltas >= 2
              ? `É a ${voltas + 1}ª vez que você volta. Isso já é padrão, e o padrão bom é que você volta.`
              : sumicoLongo
                ? `Foram ${dias} dias fora, e tudo bem. Espero que você não pegue pesado consigo — ninguém aqui está te cobrando explicação.`
                : `${dias === 1 ? 'Ontem passou em branco' : `Passaram ${dias} dias`}, e isso acontece com quase todo mundo que faz este programa. Espero que você não pegue pesado consigo.`}
        </p>

        <p style={{ fontSize: 14.5, lineHeight: 1.62, color: 'rgba(245,241,234,0.45)', margin: '16px 0 0', animation: 'habSobe2 .5s .35s both' }}>
          {/* O 0,29/42 é o custo de UM dia isolado. Repeti-lo para quem sumiu
              três semanas contradiz o próprio protocolo, que diz que uma semana
              inteira perdida "é outra coisa, com prejuízo real". */}
          {sumicoLongo ? (<>
            Semana fechada em zero é diferente de um dia perdido — essa é a única
            parte em que a evidência é dura. Mas a régua é <span style={{ color: 'var(--ember)' }}>frequência</span>,
            não passado: quatro leituras nesta semana já recolocam você na faixa.
          </>) : (<>
            {/* O sentido vem antes do número: num momento de ação, quem lê
                "0,29 numa escala de 42" primeiro só entende que levou nota. */}
            Um dia perdido quase não mexe o ponteiro — <span style={{ color: 'var(--ember)' }}>0,29
            de 42</span>, no estudo que mediu isso. Falhar no dia 5 não é pior que falhar
            no dia 50: o que pesa é o que acontece agora.
          </>)}
        </p>

        {/* O que ficou. A conquista é permanente; só o contador reinicia. */}
        {sequencia.diasLidos > 0 && (
          <div style={{
            display: 'flex', gap: 26, marginTop: 24, padding: '16px 18px',
            background: 'rgba(245,241,234,0.05)', borderRadius: 18,
            border: '1px solid rgba(245,241,234,0.09)',
            animation: 'habSobe2 .5s .45s both',
          }}>
            <div>
              <div style={{ fontFamily: serif, fontSize: 27, lineHeight: 1, color: CREME }}>{sequencia.diasLidos}</div>
              <div style={{ ...eyebrow, fontSize: 9.5, color: 'rgba(245,241,234,0.45)', marginTop: 4 }}>
                {sequencia.diasLidos === 1 ? 'dia lido' : 'dias lidos'}
              </div>
            </div>
            {sequencia.melhor > 1 && (
              <div>
                <div style={{ fontFamily: serif, fontSize: 27, lineHeight: 1, color: CREME }}>{sequencia.melhor}</div>
                <div style={{ ...eyebrow, fontSize: 9.5, color: 'rgba(245,241,234,0.45)', marginTop: 4 }}>melhor marca</div>
              </div>
            )}
            <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.5, color: 'rgba(245,241,234,0.4)', alignSelf: 'center' }}>
              Nada disso foi apagado.
            </div>
          </div>
        )}

        {/* O plano continua de pé — e é ele, não a força de vontade, que traz de volta. */}
        {contrato && (
          <div style={{ marginTop: 18, fontSize: 14, lineHeight: 1.55, color: 'rgba(245,241,234,0.55)', animation: 'habSobe2 .5s .55s both' }}>
            Seu plano continua o mesmo:{' '}
            <span style={{ color: 'var(--ember)' }}>
              {contrato.quandoKind === 'horario' ? contrato.quando : contrato.quando.replace(/^assim que eu /, 'assim que você ')}
            </span>
            , <span style={{ color: 'var(--ember)' }}>{contrato.onde}</span>.
          </div>
        )}

        {livro && (
          <div style={{ display: 'flex', gap: 13, alignItems: 'center', marginTop: 20, animation: 'habSobe2 .5s .6s both' }}>
            <BookCover book={{ title: livro.titulo, coverUrl: livro.capaUrl, coverHue: livro.capaHue, coverStyle: livro.capaEstilo } as any} width={44} height={66} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, color: CREME, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{livro.titulo}</div>
              <div style={{ fontSize: 13, color: 'rgba(245,241,234,0.5)', marginTop: 2 }}>
                te esperando na página {livro.proximaPagina}
              </div>
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '0 26px calc(22px + env(safe-area-inset-bottom,0px))' }}>
        <button onClick={onAbrirLivro} style={{
          width: '100%', padding: '17px 20px', borderRadius: 999, border: 'none',
          background: 'var(--ember)', color: '#fff', fontSize: 16, fontWeight: 600, cursor: 'pointer',
          boxShadow: '0 14px 32px -14px var(--ember)',
        }}>
          Uma página, agora
        </button>
        <button onClick={onDepois} style={{
          width: '100%', background: 'none', border: 'none', color: 'rgba(245,241,234,0.45)',
          fontSize: 14, padding: '14px 0 0', cursor: 'pointer',
        }}>
          Agora não dá
        </button>
      </div>
    </div>
  );
};

export default TelaDeRetorno;
